import React, { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Box, Typography, Paper, List, ListItem, ListItemText, Alert, Divider, Grid as MuiGrid, Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import { useUserProfileApi } from '../useUserProfileApi';

const MotionPaper = Paper; // No animation needed, but keep naming consistent
const Grid = MuiGrid as any;

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2),
  maxWidth: 800,
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    maxWidth: '100%',
  },
}));

const API_BASE_URL = 'https://erq7skufm9.execute-api.us-west-2.amazonaws.com';

function Profile() {
  const auth = useAuth();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const { profile, loading, error, fetchProfile } = useUserProfileApi(API_BASE_URL);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchProfile();
    }
    // eslint-disable-next-line
  }, [auth.isAuthenticated]);

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    // First clear the local auth state
    await auth.removeUser();
    // Then redirect to Cognito logout
    setTimeout(() => {
      const clientId = "2g373h5du3vb1c97gsp0mb8o9l";
      const logoutUri = `${window.location.origin}/`;
      const cognitoDomain = "https://auth.suliot.com";
      window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    }, 500); // short delay to show the message
  };

  if (isLoggingOut) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <CircularProgress color="secondary" sx={{ mb: 2 }} />
        <Typography variant="h6">Logging you out...</Typography>
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <StyledPaper>
        <Alert severity="warning">You must be signed in to view your profile.</Alert>
      </StyledPaper>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <CircularProgress color="primary" sx={{ mb: 2 }} />
        <Typography variant="h6">Loading your profile...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <StyledPaper>
        <Alert severity="error">{error}</Alert>
      </StyledPaper>
    );
  }

  if (!profile) {
    return (
      <StyledPaper>
        <Alert severity="info">No profile found. (You may want to create one.)</Alert>
      </StyledPaper>
    );
  }

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      <Grid container spacing={4} sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
          <MotionPaper
            sx={{
              p: { xs: 2, md: 3 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'background.paper',
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" gutterBottom>
              Profile
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="User ID (sub)" secondary={profile.sub || 'N/A'} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Email" secondary={profile.email || 'N/A'} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Name" secondary={profile.name || 'N/A'} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Given Name" secondary={profile.given_name || 'N/A'} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Family Name" secondary={profile.family_name || 'N/A'} />
              </ListItem>
              {profile.picture && (
                <ListItem>
                  <img src={profile.picture} alt="Profile" style={{ width: 64, height: 64, borderRadius: '50%' }} />
                </ListItem>
              )}
              <ListItem>
                <ListItemText primary="Birthdate" secondary={profile.birthdate || 'N/A'} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Interests" secondary={profile.interests ? profile.interests.join(', ') : 'N/A'} />
              </ListItem>
            </List>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<LogoutIcon />}
              onClick={handleSignOut}
              sx={{ mt: 2 }}
            >
              Sign Out
            </Button>
          </MotionPaper>
        </Grid>
        <Grid sx={{ gridColumn: { xs: 'span 12', md: 8 }, mt: { xs: 3, md: 0 } }}>
          <MotionPaper
            sx={{
              p: { xs: 2, md: 4 },
              backgroundColor: 'background.paper',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 3, md: 4 },
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                Authentication Details
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This information is for your reference. The ID token is used to authenticate with the backend API.
                </Typography>
              </Alert>
              <Typography variant="subtitle1" gutterBottom>
                ID Token:
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#222',
                  color: '#fff',
                  p: 2,
                  borderRadius: 1,
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  mb: 3,
                }}
              >
                {auth.user?.id_token}
              </Box>
              <Typography variant="subtitle1" gutterBottom>
                User Info:
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#222',
                  color: '#fff',
                  p: 2,
                  borderRadius: 1,
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                }}
              >
                <pre style={{ margin: 0 }}>{JSON.stringify(auth.user?.profile, null, 2)}</pre>
              </Box>
            </Box>
            <Divider />
            <Box>
              <Typography variant="h6" gutterBottom>
                Profile Data
              </Typography>
              <Box
                sx={{
                  backgroundColor: 'background.paper',
                  p: { xs: 1, sm: 2 },
                  borderRadius: 1,
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  mt: 1,
                  overflowX: 'auto',
                  width: '100%',
                  maxWidth: '100%',
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre', overflowX: 'auto' }}>{JSON.stringify(profile, null, 2)}</pre>
              </Box>
            </Box>
          </MotionPaper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Profile; 